import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Camera, Image as ImageIcon, Loader2, Check, ChevronLeft, ChevronRight, IdCard,
  Gauge, AlertTriangle, LogOut, LogIn, ArrowLeftRight, Bike, Package, WifiOff, Clock, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

const eur = (v) => `€ ${Number(v || 0).toFixed(2)}`;

// Ridimensionamento/compressione lato client: max ~3MB, qualità buona
async function compressImage(file, maxMB = 3) {
  if (!file || !file.type?.startsWith("image/")) return file;
  const dataUrl = await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(file); });
  const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = dataUrl; });
  const maxDim = 2000;
  let { width, height } = img;
  if (Math.max(width, height) > maxDim) { const sc = maxDim / Math.max(width, height); width = Math.round(width * sc); height = Math.round(height * sc); }
  const canvas = document.createElement("canvas"); canvas.width = width; canvas.height = height;
  canvas.getContext("2d").drawImage(img, 0, 0, width, height);
  const maxBytes = maxMB * 1024 * 1024;
  let q = 0.9, blob = null;
  for (let i = 0; i < 8; i++) {
    blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", q));
    if (blob && blob.size <= maxBytes) break;
    q -= 0.12; if (q < 0.3) break;
  }
  return new File([blob], (file.name || "foto").replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
}

function StoredImg({ path, alt, className }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let active = true; let obj = null;
    if (path) api.get(`/files/${path}`, { responseType: "blob" }).then((r) => { if (active) { obj = URL.createObjectURL(r.data); setUrl(obj); } }).catch(() => {});
    return () => { active = false; if (obj) URL.revokeObjectURL(obj); };
  }, [path]);
  if (!url) return <div className={`bg-zinc-900 border border-zinc-800 flex items-center justify-center text-[10px] text-zinc-600 ${className}`}>nessuna</div>;
  return <img src={url} alt={alt} className={className} />;
}

const SKIP_REASONS = ["Connessione scarsa", "Fretta / molti clienti", "Problema fotocamera", "Altro"];

function PhotoCapture({ label, rentalId, preview, onUploaded, testid, skipped, skipReason, onSkip, onUnskip }) {
  const [busy, setBusy] = useState(false);
  const handle = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const compressed = await compressImage(file, 3);
      const localUrl = URL.createObjectURL(compressed);
      const fd = new FormData(); fd.append("file", compressed);
      const res = await api.post(`/rentals/${rentalId}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(res.data.photo_path, localUrl);
      toast.success(`${label} caricata (${(compressed.size / 1024 / 1024).toFixed(1)} MB)`);
    } catch (e) { toast.error("Caricamento foto non riuscito"); }
    finally { setBusy(false); }
  };
  if (skipped) {
    return (
      <div className="rounded-sm border border-amber-500/40 bg-amber-500/10 p-3 mb-2" data-testid={`${testid}-skipped`}>
        <div className="flex items-center gap-2 text-amber-400 text-sm font-semibold mb-2"><AlertTriangle className="h-4 w-4" /> Foto saltata</div>
        <div className="text-xs text-zinc-400 mb-2">Motivo:</div>
        <Select value={skipReason || SKIP_REASONS[0]} onValueChange={(v) => onSkip(v)}>
          <SelectTrigger className="bg-[#0A0A0A] border-zinc-800 rounded-sm h-9 w-full mb-2" data-testid={`${testid}-skip-reason`}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-zinc-800">{SKIP_REASONS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <button type="button" onClick={onUnskip} data-testid={`${testid}-unskip`} className="text-xs text-[#f98125] hover:underline">Annulla · scatta comunque la foto</button>
      </div>
    );
  }
  return (
    <div>
      {preview ? (
        <div className="relative rounded-sm overflow-hidden border border-[#39FF14]/40 mb-2">
          <img src={preview} alt={label} className="w-full max-h-64 object-contain bg-black" />
          <span className="absolute top-2 right-2 bg-[#39FF14] text-black text-[10px] font-bold px-2 py-0.5 rounded-sm flex items-center gap-1"><Check className="h-3 w-3" /> OK</span>
        </div>
      ) : (
        <div className="rounded-sm border border-dashed border-zinc-700 bg-zinc-900/40 h-40 flex items-center justify-center text-zinc-600 mb-2 text-sm">{busy ? <Loader2 className="h-6 w-6 animate-spin text-[#f98125]" /> : "Nessuna foto"}</div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <label className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-sm border border-[#f98125]/50 bg-[#f98125]/10 cursor-pointer hover:bg-[#f98125]/20 text-sm text-white">
          <Camera className="h-4 w-4" /> Fotocamera
          <input type="file" accept="image/*" capture="environment" className="hidden" data-testid={`${testid}-camera`} onChange={(e) => handle(e.target.files?.[0])} />
        </label>
        <label className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-sm border border-zinc-800 cursor-pointer hover:border-zinc-700 text-sm text-zinc-400">
          <ImageIcon className="h-4 w-4" /> Galleria
          <input type="file" accept="image/*" className="hidden" data-testid={`${testid}-gallery`} onChange={(e) => handle(e.target.files?.[0])} />
        </label>
      </div>
      {onSkip && !preview && (
        <button type="button" onClick={() => onSkip(SKIP_REASONS[0])} data-testid={`${testid}-skip`}
          className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm border border-dashed border-amber-500/40 text-amber-400 text-xs hover:bg-amber-500/10">
          <WifiOff className="h-3.5 w-3.5" /> Salta foto (connessione scarsa)
        </button>
      )}
    </div>
  );
}

function WizardShell({ open, onOpenChange, icon, title, description, steps, current, setCurrent, onFinish, finishLabel, finishColor, canNext, testidPrefix, children }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141414] border-zinc-800 max-w-lg max-h-[92vh] overflow-y-auto" data-testid={`${testidPrefix}-wizard`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">{icon} {title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-1.5 my-2">
          {steps.map((s, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= current ? "bg-[#f98125]" : "bg-zinc-800"}`} />
          ))}
        </div>
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Passo {current + 1}/{steps.length} · {steps[current]}</div>
        <div className="py-1 min-h-[200px]">{children}</div>
        <div className="flex items-center justify-between gap-2 pt-3 border-t border-zinc-800">
          <button onClick={() => current === 0 ? onOpenChange(false) : setCurrent(current - 1)} data-testid={`${testidPrefix}-back`}
            className="flex items-center gap-1 rounded-sm border border-zinc-700 px-4 py-2.5 text-sm hover:bg-zinc-800">
            <ChevronLeft className="h-4 w-4" /> {current === 0 ? "Annulla" : "Indietro"}
          </button>
          {current < steps.length - 1 ? (
            <button onClick={() => canNext() && setCurrent(current + 1)} data-testid={`${testidPrefix}-next`}
              className="flex items-center gap-1 rounded-sm bg-[#f98125] text-white font-bold px-5 py-2.5 text-sm hover:bg-[#d96a12]">
              Avanti <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button onClick={() => canNext() && onFinish()} data-testid={`${testidPrefix}-finish`}
              className={`flex items-center gap-1 rounded-sm font-bold px-5 py-2.5 text-sm ${finishColor}`}>
              <Check className="h-4 w-4" /> {finishLabel}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MoneyMethod({ label, amount, setAmount, method, setMethod, methods, testid }) {
  return (
    <div className="grid grid-cols-2 gap-3 items-end">
      <div><Label className="text-xs uppercase tracking-wider text-zinc-500">{label} €</Label>
        <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid={`${testid}-amount`} /></div>
      <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Metodo</Label>
        <Select value={method} onValueChange={setMethod}>
          <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid={`${testid}-method`}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-zinc-800">{methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </div>
  );
}

export function CheckoutWizard({ rental, methods, accName, onClose, onDone }) {
  const bikes = (rental.bici && rental.bici.length ? rental.bici : [{ bike_id: rental.bike_id, bike_numero: rental.bike_numero }]);
  const multi = bikes.length > 1;
  const [step, setStep] = useState(0);
  const [doc, setDoc] = useState({ path: null, url: null, skipped: false, reason: "" });
  const [bd, setBd] = useState(bikes.map((b) => ({ bike_id: b.bike_id, bike_numero: b.bike_numero, dx: { path: null, url: null, skipped: false, reason: "" }, sx: { path: null, url: null, skipped: false, reason: "" }, km: "" })));
  const accLines = (rental.accessori || []).map((x) => (typeof x === "string" ? { id: x, qta: 1 } : { id: x.id || x.accessorio, qta: x.qta || x.quantita || 1 }));
  const [accSel, setAccSel] = useState(accLines.map((l) => l.id));
  const accLabel = (l) => `${accName(l.id)}${l.qta > 1 ? ` ×${l.qta}` : ""}`;
  const [firma, setFirma] = useState(false);
  const [note, setNote] = useState("");
  const [privacy, setPrivacy] = useState(false);
  const [mkt, setMkt] = useState(false);
  const [minore, setMinore] = useState(false);
  const [minoreNome, setMinoreNome] = useState("");
  const [minoreNascita, setMinoreNascita] = useState("");
  const [tutoreNome, setTutoreNome] = useState("");
  const cauzioneDefault = rental.preventivo?.cauzione || 0;
  const saldoDefault = rental.canale === "online" ? (rental.saldo_in_sede || 0) : (rental.preventivo?.totale || 0);
  const [saldo, setSaldo] = useState(String(saldoDefault || 0));
  const [saldoM, setSaldoM] = useState(methods[0] || "Contanti");
  const [caz, setCaz] = useState(String(cauzioneDefault || 0));
  const [cazM, setCazM] = useState(methods[0] || "Contanti");
  const [cazInc, setCazInc] = useState(true);
  const [busy, setBusy] = useState(false);

  const STEPS = ["Documento", ...bikes.map((b) => `Bici ${b.bike_numero}`), "Consensi e manleva", "Accessori e incasso"];
  const bikeStart = 1;
  const consentStep = 1 + bikes.length;
  const moneyStep = 2 + bikes.length;
  const upd = (i, patch) => setBd((s) => s.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));

  const canNext = () => {
    if (step === 0 && !doc.path && !doc.skipped) { toast.error("Scatta/carica la foto del documento, oppure usa «Salta foto»"); return false; }
    if (step >= bikeStart && step < consentStep) {
      const b = bd[step - bikeStart];
      if (!b.dx.path && !b.dx.skipped) { toast.error(`Bici ${b.bike_numero}: carica la foto del lato destro o saltala`); return false; }
      if (!b.sx.path && !b.sx.skipped) { toast.error(`Bici ${b.bike_numero}: carica la foto del lato sinistro o saltala`); return false; }
      if (b.km === "" || isNaN(+b.km)) { toast.error(`Bici ${b.bike_numero}: inserisci i km iniziali`); return false; }
    }
    if (step === consentStep) {
      if (!privacy) { toast.error("Il consenso al trattamento dati (privacy) è obbligatorio"); return false; }
      if (minore && (!minoreNome.trim() || !tutoreNome.trim())) { toast.error("Per la manleva minori inserisci nome del minore e del tutore"); return false; }
    }
    return true;
  };
  const finish = async () => {
    if (!privacy) { toast.error("Il consenso privacy è obbligatorio"); setStep(consentStep); return; }
    setBusy(true);
    const pagamenti = [];
    if (+saldo > 0) pagamenti.push({ tipo: "saldo", importo: +saldo, metodo: saldoM });
    if (cazInc && +caz > 0) pagamenti.push({ tipo: "cauzione", importo: +caz, metodo: cazM });
    try {
      await api.post(`/rentals/${rental.id}/checkout`, {
        documento_path: doc.path,
        documento_omesso: !doc.path && doc.skipped, documento_omessa_motivo: doc.reason || "",
        bici: bd.map((b) => ({
          bike_id: b.bike_id, bike_numero: b.bike_numero, km_iniziali: parseInt(b.km),
          foto_lato_dx: b.dx.path, foto_lato_sx: b.sx.path,
          foto_dx_omessa: !b.dx.path && b.dx.skipped, foto_dx_omessa_motivo: b.dx.reason || "",
          foto_sx_omessa: !b.sx.path && b.sx.skipped, foto_sx_omessa_motivo: b.sx.reason || "",
        })),
        accessori_consegnati: accLines.filter((l) => accSel.includes(l.id)).map((l) => accLabel(l)), cauzione_incassata: cazInc, firma_cliente: firma, pagamenti, note,
        consenso_privacy: privacy, consenso_marketing: mkt,
        manleva_minore: minore, minore_nome: minoreNome, minore_data_nascita: minoreNascita, tutore_nome: tutoreNome,
      });
      toast.success("Check-out registrato · noleggio in corso");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); setBusy(false); }
  };

  const bi = step - bikeStart;
  const cb = step >= bikeStart && step < consentStep ? bd[bi] : null;

  return (
    <WizardShell open onOpenChange={(v) => !v && onClose()} icon={<LogOut className="h-5 w-5 text-[#f98125]" />}
      title={multi ? `Check-out · ${bikes.length} bici (${rental.customer_nome || ""})` : `Check-out · Bici ${bikes[0].bike_numero}`}
      description="Procedura guidata di consegna. Le foto vengono compresse a max 3 MB."
      steps={STEPS} current={step} setCurrent={setStep} onFinish={busy ? () => {} : finish} finishLabel={busy ? "Salvataggio…" : "Conferma consegna"}
      finishColor="bg-[#f98125] text-white hover:bg-[#d96a12]" canNext={canNext} testidPrefix="checkout">
      {step === 0 && (<div className="space-y-2"><Label className="text-sm text-zinc-300 flex items-center gap-2"><IdCard className="h-4 w-4 text-[#f98125]" /> Documento d'identità del cliente</Label>
        <PhotoCapture label="Documento" rentalId={rental.id} preview={doc.url} onUploaded={(p, u) => setDoc({ path: p, url: u, skipped: false, reason: "" })} testid="checkout-doc"
          skipped={doc.skipped} skipReason={doc.reason} onSkip={(m) => setDoc({ path: null, url: null, skipped: true, reason: m })} onUnskip={() => setDoc({ path: null, url: null, skipped: false, reason: "" })} /></div>)}
      {cb && (<div className="space-y-3" data-testid={`checkout-bike-${bi}`}>
        <div className="text-sm text-[#f98125] font-semibold flex items-center gap-2"><Bike className="h-4 w-4" /> Bici {cb.bike_numero} {multi && <span className="text-zinc-500 font-normal">({bi + 1} di {bikes.length})</span>}</div>
        <div><Label className="text-sm text-zinc-300 mb-2 block">Foto LATO DESTRO</Label>
          <PhotoCapture key={`co-dx-${bi}`} label="Lato destro" rentalId={rental.id} preview={cb.dx.url} onUploaded={(p, u) => upd(bi, { dx: { path: p, url: u, skipped: false, reason: "" } })} testid={`checkout-dx-${bi}`}
            skipped={cb.dx.skipped} skipReason={cb.dx.reason} onSkip={(m) => upd(bi, { dx: { path: null, url: null, skipped: true, reason: m } })} onUnskip={() => upd(bi, { dx: { path: null, url: null, skipped: false, reason: "" } })} /></div>
        <div><Label className="text-sm text-zinc-300 mb-2 block">Foto LATO SINISTRO</Label>
          <PhotoCapture key={`co-sx-${bi}`} label="Lato sinistro" rentalId={rental.id} preview={cb.sx.url} onUploaded={(p, u) => upd(bi, { sx: { path: p, url: u, skipped: false, reason: "" } })} testid={`checkout-sx-${bi}`}
            skipped={cb.sx.skipped} skipReason={cb.sx.reason} onSkip={(m) => upd(bi, { sx: { path: null, url: null, skipped: true, reason: m } })} onUnskip={() => upd(bi, { sx: { path: null, url: null, skipped: false, reason: "" } })} /></div>
        <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Km iniziali (dal display) *</Label>
          <Input type="number" data-testid={`checkout-km-${bi}`} value={cb.km} onChange={(e) => upd(bi, { km: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
      </div>)}
      {step === consentStep && (<div className="space-y-4" data-testid="checkout-consensi">
        <div className="rounded-sm border border-zinc-800 bg-[#0A0A0A] p-3 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div><span className="text-sm text-zinc-200 font-medium">Manleva per minori</span><p className="text-[11px] text-zinc-500 mt-0.5">Attiva solo se tra gli utilizzatori c'è un minore.</p></div>
            <Switch checked={minore} onCheckedChange={setMinore} data-testid="checkout-minore" />
          </div>
          {minore && (<div className="grid grid-cols-1 gap-2 pt-1">
            <Input placeholder="Nome e cognome del minore *" value={minoreNome} onChange={(e) => setMinoreNome(e.target.value)} className="bg-[#141414] border-zinc-800 rounded-sm" data-testid="checkout-minore-nome" />
            <Input type="date" value={minoreNascita} onChange={(e) => setMinoreNascita(e.target.value)} className="bg-[#141414] border-zinc-800 rounded-sm" data-testid="checkout-minore-nascita" />
            <Input placeholder="Nome e cognome del genitore/tutore *" value={tutoreNome} onChange={(e) => setTutoreNome(e.target.value)} className="bg-[#141414] border-zinc-800 rounded-sm" data-testid="checkout-tutore-nome" />
          </div>)}
        </div>
        <label className="flex items-start gap-3 cursor-pointer rounded-sm border border-zinc-800 p-3" data-testid="checkout-privacy-label">
          <Checkbox checked={privacy} onCheckedChange={setPrivacy} data-testid="checkout-privacy" className="mt-0.5" />
          <span className="text-sm text-zinc-300">Il cliente acconsente al <b>trattamento dei dati personali</b> (privacy). <span className="text-[#f98125]">Obbligatorio *</span></span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer rounded-sm border border-zinc-800 p-3" data-testid="checkout-marketing-label">
          <Checkbox checked={mkt} onCheckedChange={setMkt} data-testid="checkout-marketing" className="mt-0.5" />
          <span className="text-sm text-zinc-300">Il cliente acconsente a ricevere <b>comunicazioni di marketing</b> (facoltativo).</span>
        </label>
      </div>)}
      {step === moneyStep && (<div className="space-y-4">
        {accLines.length > 0 && (<div><Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Accessori consegnati</Label>
          <div className="grid grid-cols-2 gap-2">{accLines.map((l) => (
            <label key={l.id} className={`flex items-center gap-2 px-3 py-2 rounded-sm border cursor-pointer text-sm ${accSel.includes(l.id) ? "border-[#f98125] bg-[#f98125]/10" : "border-zinc-800"}`} data-testid={`checkout-acc-${l.id}`}>
              <Checkbox checked={accSel.includes(l.id)} onCheckedChange={() => setAccSel((s) => s.includes(l.id) ? s.filter((x) => x !== l.id) : [...s, l.id])} />{accLabel(l)}
            </label>))}</div></div>)}
        <MoneyMethod label="Incasso noleggio" amount={saldo} setAmount={setSaldo} method={saldoM} setMethod={setSaldoM} methods={methods} testid="checkout-saldo" />
        <div className="flex items-center justify-between"><span className="text-sm text-zinc-300">Cauzione incassata</span><Switch checked={cazInc} onCheckedChange={setCazInc} data-testid="checkout-cauzione" /></div>
        {cazInc && <MoneyMethod label="Cauzione" amount={caz} setAmount={setCaz} method={cazM} setMethod={setCazM} methods={methods} testid="checkout-caz" />}
        <div className="flex items-center justify-between"><span className="text-sm text-zinc-300">Firma / conferma cliente</span><Switch checked={firma} onCheckedChange={setFirma} data-testid="checkout-firma" /></div>
        <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Note</Label><Textarea data-testid="checkout-note" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" rows={2} /></div>
      </div>)}
    </WizardShell>
  );
}

export function CheckinWizard({ rental, methods, accName, onClose, onDone }) {
  const bikes = (rental.bici && rental.bici.length ? rental.bici : [{ bike_id: rental.bike_id, bike_numero: rental.bike_numero }]);
  const multi = bikes.length > 1;
  const co = rental.checkout || {};
  const coBikes = co.bici && co.bici.length ? co.bici : [{ bike_id: rental.bike_id, bike_numero: rental.bike_numero, km_iniziali: co.km_iniziali || 0, foto_lato_dx: co.foto_lato_dx, foto_lato_sx: co.foto_lato_sx }];
  const coMap = Object.fromEntries(coBikes.map((b) => [b.bike_id, b]));
  const accLines = (rental.accessori || []).map((x) => (typeof x === "string" ? { id: x, qta: 1 } : { id: x.id || x.accessorio, qta: x.qta || x.quantita || 1 }));
  const hasAcc = accLines.length > 0;
  const nameOf = accName || ((id) => id);
  const [step, setStep] = useState(0);
  const [bd, setBd] = useState(bikes.map((b) => ({
    bike_id: b.bike_id, bike_numero: b.bike_numero,
    dx: { path: null, url: null, skipped: false, reason: "" }, sx: { path: null, url: null, skipped: false, reason: "" }, km: "",
    danni: false, dDesc: "", dGrav: "media",
  })));
  const [resi, setResi] = useState(accLines.map((l) => ({ id: l.id, qta_consegnata: l.qta, qta_resa: l.qta, motivo: "", nota: "" })));
  const updResi = (i, patch) => setResi((s) => s.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const cauzione = rental.preventivo?.cauzione || 0;
  const [cazRest, setCazRest] = useState(true);
  const [trat, setTrat] = useState("0");
  const [tratM, setTratM] = useState(methods[0] || "Contanti");
  const [saldo, setSaldo] = useState(String(rental.canale === "online" ? (rental.saldo_in_sede || 0) : 0));
  const [saldoM, setSaldoM] = useState(methods[0] || "Contanti");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  // Proposta adeguamento tariffa (upgrade fascia) in base alla durata effettiva
  const [adeg, setAdeg] = useState(null);
  const [applyAdeg, setApplyAdeg] = useState(false);
  const [adegM, setAdegM] = useState(methods[0] || "Contanti");
  useEffect(() => {
    api.get(`/rentals/${rental.id}/checkin-tariffa-proposta`)
      .then((r) => { if (r.data?.applicabile) setAdeg(r.data); })
      .catch(() => {});
  }, []);

  const STEPS = [...bikes.map((b) => `Bici ${b.bike_numero}`), ...(hasAcc ? ["Rientro accessori"] : []), "Cauzione e saldo"];
  const accStep = hasAcc ? bikes.length : -1;
  const moneyStep = bikes.length + (hasAcc ? 1 : 0);
  const upd = (i, patch) => setBd((s) => s.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const kmInizOf = (id) => Number(coMap[id]?.km_iniziali || 0);

  const canNext = () => {
    if (step < bikes.length) {
      const b = bd[step]; const ki = kmInizOf(b.bike_id);
      if (!b.dx.path && !b.dx.skipped) { toast.error(`Bici ${b.bike_numero}: carica la foto del lato destro o saltala`); return false; }
      if (!b.sx.path && !b.sx.skipped) { toast.error(`Bici ${b.bike_numero}: carica la foto del lato sinistro o saltala`); return false; }
      if (b.km === "" || isNaN(+b.km)) { toast.error(`Bici ${b.bike_numero}: inserisci i km finali`); return false; }
      if (+b.km < ki) { toast.error(`Bici ${b.bike_numero}: i km finali non possono essere < ${ki}`); return false; }
      if (b.danni && !b.dDesc.trim()) { toast.error(`Bici ${b.bike_numero}: descrivi il danno`); return false; }
    }
    if (step === accStep) {
      for (const r of resi) {
        if (r.qta_resa < r.qta_consegnata && !r.motivo) { toast.error(`${nameOf(r.id)}: indica il motivo del reso parziale`); return false; }
      }
    }
    return true;
  };
  const finish = async () => {
    setBusy(true);
    const pagamenti = [];
    if (+saldo > 0) pagamenti.push({ tipo: "saldo", importo: +saldo, metodo: saldoM });
    if (+trat > 0) pagamenti.push({ tipo: "cauzione_trattenuta", importo: +trat, metodo: tratM });
    try {
      await api.post(`/rentals/${rental.id}/checkin`, {
        bici: bd.map((b) => ({ bike_id: b.bike_id, bike_numero: b.bike_numero, km_finali: parseInt(b.km),
          foto_lato_dx: b.dx.path, foto_lato_sx: b.sx.path,
          foto_dx_omessa: !b.dx.path && b.dx.skipped, foto_dx_omessa_motivo: b.dx.reason || "",
          foto_sx_omessa: !b.sx.path && b.sx.skipped, foto_sx_omessa_motivo: b.sx.reason || "",
          danni: b.danni, danno_descrizione: b.dDesc, danno_gravita: b.dGrav })),
        accessori_resi: resi.map((r) => ({ id: r.id, qta_resa: r.qta_resa, motivo: r.qta_resa < r.qta_consegnata ? r.motivo : "", nota: r.nota })),
        cauzione_restituita: cazRest, cauzione_trattenuta: +trat || 0, saldo_incassato: +saldo || 0,
        adeguamento_tariffa: !!(applyAdeg && adeg?.applicabile), adeguamento_metodo: adegM,
        pagamenti, note,
      });
      toast.success("Check-in registrato · noleggio concluso");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); setBusy(false); }
  };

  const Compare = ({ side, beforePath, after }) => (
    <div>
      <Label className="text-sm text-zinc-300 mb-2 block flex items-center gap-2"><ArrowLeftRight className="h-4 w-4 text-[#f98125]" /> Confronto {side}</Label>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div><div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Prima (consegna)</div>
          <StoredImg path={beforePath} alt={`prima ${side}`} className="w-full h-32 object-contain bg-black rounded-sm border border-zinc-800" /></div>
        <div><div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Dopo (rientro)</div>
          {after ? <img src={after} alt={`dopo ${side}`} className="w-full h-32 object-contain bg-black rounded-sm border border-[#39FF14]/40" /> : <div className="w-full h-32 bg-zinc-900 border border-dashed border-zinc-700 rounded-sm flex items-center justify-center text-[10px] text-zinc-600">scatta sotto</div>}</div>
      </div>
    </div>
  );

  const b = step < bikes.length ? bd[step] : null;
  const cob = b ? coMap[b.bike_id] || {} : {};
  const kmIniz = b ? kmInizOf(b.bike_id) : 0;
  const kmPerc = b && b.km !== "" && !isNaN(+b.km) ? Math.max(0, (+b.km) - kmIniz) : null;

  return (
    <WizardShell open onOpenChange={(v) => !v && onClose()} icon={<LogIn className="h-5 w-5 text-[#39FF14]" />}
      title={multi ? `Check-in · ${bikes.length} bici (${rental.customer_nome || ""})` : `Check-in · Bici ${bikes[0].bike_numero}`}
      description="Procedura guidata di rientro: confronta foto prima/dopo e gestisci danni, cauzione e saldo."
      steps={STEPS} current={step} setCurrent={setStep} onFinish={busy ? () => {} : finish} finishLabel={busy ? "Salvataggio…" : "Conferma rientro"}
      finishColor="bg-[#39FF14] text-[#0A0A0A] hover:bg-[#2dd60f]" canNext={canNext} testidPrefix="checkin">
      {b && (<div className="space-y-4" data-testid={`checkin-bike-${step}`}>
        <div className="text-sm text-[#39FF14] font-semibold flex items-center gap-2"><Bike className="h-4 w-4" /> Bici {b.bike_numero} {multi && <span className="text-zinc-500 font-normal">({step + 1} di {bikes.length})</span>}</div>
        <div><Compare side="LATO DESTRO" beforePath={cob.foto_lato_dx} after={b.dx.url} />
          <PhotoCapture key={`ci-dx-${step}`} label="Lato destro" rentalId={rental.id} preview={b.dx.url} onUploaded={(p, u) => upd(step, { dx: { path: p, url: u, skipped: false, reason: "" } })} testid={`checkin-dx-${step}`}
            skipped={b.dx.skipped} skipReason={b.dx.reason} onSkip={(m) => upd(step, { dx: { path: null, url: null, skipped: true, reason: m } })} onUnskip={() => upd(step, { dx: { path: null, url: null, skipped: false, reason: "" } })} /></div>
        <div><Compare side="LATO SINISTRO" beforePath={cob.foto_lato_sx} after={b.sx.url} />
          <PhotoCapture key={`ci-sx-${step}`} label="Lato sinistro" rentalId={rental.id} preview={b.sx.url} onUploaded={(p, u) => upd(step, { sx: { path: p, url: u, skipped: false, reason: "" } })} testid={`checkin-sx-${step}`}
            skipped={b.sx.skipped} skipReason={b.sx.reason} onSkip={(m) => upd(step, { sx: { path: null, url: null, skipped: true, reason: m } })} onUnskip={() => upd(step, { sx: { path: null, url: null, skipped: false, reason: "" } })} /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Km iniziali</Label><Input value={kmIniz} disabled className="mt-1.5 bg-[#0A0A0A]/50 border-zinc-800 rounded-sm text-zinc-500" /></div>
          <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Km finali *</Label><Input type="number" data-testid={`checkin-km-${step}`} value={b.km} onChange={(e) => upd(step, { km: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
        </div>
        {kmPerc != null && <div className="text-sm text-[#39FF14] flex items-center gap-2" data-testid={`checkin-kmperc-${step}`}><Gauge className="h-4 w-4" /> Km percorsi: <b>{kmPerc} km</b></div>}
        <div className="flex items-center justify-between"><span className="text-sm text-zinc-300 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-[#FF3B30]" /> Danni rilevati su questa bici</span><Switch checked={b.danni} onCheckedChange={(v) => upd(step, { danni: v })} data-testid={`checkin-danni-${step}`} /></div>
        {b.danni && (<div className="space-y-2 border border-[#FF3B30]/30 rounded-sm p-3">
          <Textarea data-testid={`checkin-danno-desc-${step}`} value={b.dDesc} onChange={(e) => upd(step, { dDesc: e.target.value })} className="bg-[#0A0A0A] border-zinc-800 rounded-sm" rows={2} placeholder="Descrizione del danno (creerà una segnalazione per questa bici)" />
          <Select value={b.dGrav} onValueChange={(v) => upd(step, { dGrav: v })}><SelectTrigger className="bg-[#0A0A0A] border-zinc-800 rounded-sm h-9 w-40" data-testid={`checkin-danno-gravita-${step}`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#141414] border-zinc-800"><SelectItem value="bassa">Bassa</SelectItem><SelectItem value="media">Media</SelectItem><SelectItem value="alta">Alta</SelectItem></SelectContent></Select>
        </div>)}
      </div>)}
      {step === accStep && (<div className="space-y-4" data-testid="checkin-accessori-step">
        <div className="text-sm text-[#39FF14] font-semibold flex items-center gap-2"><Package className="h-4 w-4" /> Rientro accessori</div>
        <p className="text-xs text-zinc-500">Indica la quantità restituita per ogni accessorio. Gli accessori integri tornano disponibili; quelli mancanti (smarriti/danneggiati) verranno scalati dall'inventario.</p>
        {resi.map((r, i) => {
          const parziale = r.qta_resa < r.qta_consegnata;
          return (
            <div key={r.id} className="border border-zinc-800 rounded-sm p-3 space-y-2" data-testid={`checkin-acc-${r.id}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-200">{nameOf(r.id)} <span className="text-zinc-500">· consegnati {r.qta_consegnata}</span></span>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => updResi(i, { qta_resa: Math.max(0, r.qta_resa - 1) })} className="h-7 w-7 rounded-sm border border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid={`checkin-acc-minus-${r.id}`}>−</button>
                  <span className="w-8 text-center text-sm" data-testid={`checkin-acc-qta-${r.id}`}>{r.qta_resa}</span>
                  <button type="button" onClick={() => updResi(i, { qta_resa: Math.min(r.qta_consegnata, r.qta_resa + 1) })} className="h-7 w-7 rounded-sm border border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid={`checkin-acc-plus-${r.id}`}>+</button>
                  <span className="text-xs text-zinc-500">resi</span>
                </div>
              </div>
              {parziale && (<div className="space-y-2 border-t border-zinc-800 pt-2">
                <div className="text-xs text-amber-400">Reso parziale ({r.qta_consegnata - r.qta_resa} mancante/i): indica il motivo</div>
                <Select value={r.motivo} onValueChange={(v) => updResi(i, { motivo: v })}><SelectTrigger className="bg-[#0A0A0A] border-zinc-800 rounded-sm h-9 w-48" data-testid={`checkin-acc-motivo-${r.id}`}><SelectValue placeholder="Scegli motivo…" /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-zinc-800"><SelectItem value="smarrito">Smarrito</SelectItem><SelectItem value="danneggiato">Danneggiato</SelectItem><SelectItem value="altro">Altro</SelectItem></SelectContent></Select>
                <Input value={r.nota} onChange={(e) => updResi(i, { nota: e.target.value })} placeholder="Nota (facoltativa)" className="bg-[#0A0A0A] border-zinc-800 rounded-sm h-9" data-testid={`checkin-acc-nota-${r.id}`} />
              </div>)}
            </div>
          );
        })}
      </div>)}
      {step === moneyStep && (<div className="space-y-4">
        {adeg?.applicabile && (
          <div className="rounded-sm border border-[#f98125]/50 bg-[#f98125]/10 p-3 space-y-3" data-testid="checkin-adeg-card">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#f98125]"><TrendingUp className="h-4 w-4" /> Adeguamento tariffa consigliato</div>
            <p className="text-xs text-zinc-300 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-zinc-500" /> Utilizzo effettivo <b>{adeg.durata_ore} h</b>: eccede la fascia prenotata (<b>{adeg.tariffa_attuale}</b>). Tariffa consigliata: <b>{adeg.tariffa_proposta}</b>.</p>
            <div className="flex items-center justify-between text-sm bg-[#0A0A0A] border border-zinc-800 rounded-sm px-3 py-2">
              <span className="text-zinc-400">{eur(adeg.prezzo_attuale)} → <b className="text-white">{eur(adeg.prezzo_proposto)}</b></span>
              <span className="text-[#39FF14] font-semibold" data-testid="checkin-adeg-diff">Differenza da incassare: {eur(adeg.differenza)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-300">Applica la nuova tariffa e incassa la differenza</span>
              <Switch checked={applyAdeg} onCheckedChange={setApplyAdeg} data-testid="checkin-adeg-apply" />
            </div>
            {applyAdeg && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Metodo incasso differenza</Label>
                <Select value={adegM} onValueChange={setAdegM}>
                  <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="checkin-adeg-method"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-zinc-800">{methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <p className="text-[11px] text-zinc-500">Nessun addebito automatico: applica solo se confermi. In alternativa lascia invariato.</p>
          </div>
        )}
        {cauzione > 0 && <div className="text-xs text-zinc-500">Cauzione versata alla consegna: <b className="text-zinc-300">{eur(cauzione)}</b></div>}
        <div className="flex items-center justify-between"><span className="text-sm text-zinc-300">Cauzione restituita al cliente</span><Switch checked={cazRest} onCheckedChange={setCazRest} data-testid="checkin-cauzione" /></div>
        <MoneyMethod label="Cauzione trattenuta" amount={trat} setAmount={setTrat} method={tratM} setMethod={setTratM} methods={methods} testid="checkin-trattenuta" />
        <MoneyMethod label="Saldo incassato ora" amount={saldo} setAmount={setSaldo} method={saldoM} setMethod={setSaldoM} methods={methods} testid="checkin-saldo" />
        <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Note</Label><Textarea data-testid="checkin-note" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" rows={2} /></div>
      </div>)}
    </WizardShell>
  );
}
